import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import type { NurseOption, SwapOvertime, SwapSideData } from "../../hooks/useSwapRequest";
import type { TranslationKeys } from "../../i18n/types";
import { INK, STATUS } from "../../theme/tokens";
import type { NurseInShift, OvertimeKind } from "../../utils/models";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemDatePickerPopup } from "../UIToolkit/Popups/SystemDatePickerPopup";

export type SwapSideProps = {
	title: string;
	side: SwapSideData;
	date: DateTime | null;
	onDateChange: (date: DateTime | null) => void;
	referenceDate?: DateTime | null;
	nurseOptions: SystemAutoCompleteOption<NurseOption>[];
	shiftOptions: SystemAutoCompleteOption<NurseInShift>[];
	hasShiftInDate: (nurseId: number, date: DateTime) => boolean;
	dateRange: { start: DateTime; end: DateTime };
	disabled?: boolean;
	disablePortal?: boolean;
	errorText?: string;
	sx?: SxProps<Theme>;
	jobManagementEnabled?: boolean;
};

const formatShiftLabel = (shift: NurseInShift, jobManagementEnabled?: boolean): string => {
	const jobPrefix = jobManagementEnabled && shift.jobName ? `${shift.jobName} - ` : "";
	return shift.startTime && shift.endTime ? `${jobPrefix}${shift.startTime.toFormat("h:mm a")} - ${shift.endTime.toFormat("h:mm a")}` : "";
};

const autoCompleteTextFieldStyles = {
	formControlSx: {
		width: "100%",
		"& .MuiInputLabel-root": {
			transform: "translate(0.875rem, 1rem) scale(1)",
		},
		"& .MuiInputLabel-root.MuiInputLabel-shrink": {
			transform: "translate(0.875rem, -0.5625rem) scale(0.75)",
		},
	},
	inputSx: {
		"& .MuiOutlinedInput-root": {
			minHeight: 50,
		},
	},
} as const;

/** DAT-289: the kind is read off the prediction, never assumed. The predicted delta is a MAX across
 * weekly, daily and consecutive caps, so this line called a part-timer's 7th-consecutive-day hours
 * "weekly overtime" even though her week was nowhere near 40h. */
const OT_KIND_KEYS: Record<OvertimeKind, TranslationKeys> = {
	weekly: "swap.overtime.kind.weekly",
	daily: "swap.overtime.kind.daily",
	consecutive: "swap.overtime.kind.consecutive",
};

export const OvertimeText = ({ ot }: { ot: SwapOvertime }) => {
	const { t } = useTranslation();
	// `null` is "this swap moves no overtime" — the only state with no kind to name, so there is
	// nothing to render and no need for a placeholder kind anywhere upstream.
	if (!ot) return null;

	const kind = t(OT_KIND_KEYS[ot.kind]);
	const hours = Math.abs(ot.hours).toFixed(1);
	return (
		<Typography
			sx={{
				fontSize: 12,
				color: ot.hours > 0 ? STATUS.ERROR.main : STATUS.SUCCESS.main,
			}}
		>
			{ot.hours > 0 ? t("swap.overtime.cause", { hours, kind }) : t("swap.overtime.reduce", { hours, kind })}
		</Typography>
	);
};

export const SwapSide = ({
	title,
	side,
	date,
	onDateChange,
	referenceDate,
	nurseOptions,
	shiftOptions,
	hasShiftInDate,
	dateRange,
	disabled = false,
	disablePortal = false,
	errorText,
	sx,
	jobManagementEnabled,
}: SwapSideProps) => {
	const datePickerAnchorEl = useRef<HTMLDivElement>(null);
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2, ...(sx ?? {}) }}>
			<Typography sx={{ fontSize: 16, fontWeight: 600, color: INK.SECONDARY }}>{title}</Typography>
			<Box
				sx={{
					width: "100%",
					display: "flex",
					flexDirection: "column",
					gap: 2,
				}}
			>
				<SystemAutoComplete
					fullWidth
					value={side.nurse ? { label: side.nurse.label, value: side.nurse } : null}
					onChange={(_, newValue) => side.onNurseChange(newValue?.value ?? null)}
					disabled={disabled}
					options={nurseOptions}
					textFieldProps={
						errorText
							? {
									...autoCompleteTextFieldStyles,
									label: "Select staff",
									placeholder: "Search staff member",
									reserveHeightForHelperText: true,
									defaultHelperText: errorText,
									defaultBorderColor: STATUS.ERROR.main,
								}
							: {
									...autoCompleteTextFieldStyles,
									label: "Select staff",
									placeholder: "Search staff member",
									reserveHeightForHelperText: false,
								}
					}
				/>
				<SystemDatePickerPopup
					anchorEl={datePickerAnchorEl.current}
					disablePortal={disablePortal}
					value={date}
					onChange={onDateChange}
					shouldDisableDate={(dayDate) => !hasShiftInDate(side.nurse?.id ?? 0, dayDate)}
					hideTextField={false}
					hasHoverBackground
					overridePickerProps={{
						referenceDate: date ?? referenceDate ?? dateRange.start,
						disabled: disabled || !side.nurse,
						label: "Select date",
						minDate: dateRange.start,
						maxDate: dateRange.end,
						slotProps: {
							textField: {
								sx: {
									width: "100%",
									...autoCompleteTextFieldStyles.inputSx,
								},
							},
						},
					}}
				/>
				<SystemAutoComplete
					fullWidth
					value={side.shift ? { label: formatShiftLabel(side.shift, jobManagementEnabled), value: side.shift } : null}
					onChange={(_, newValue) => side.onShiftChange(newValue?.value ?? null)}
					options={shiftOptions}
					textFieldProps={{
						...autoCompleteTextFieldStyles,
						label: "Select shift",
						placeholder: `Choose shift to swap ${title === "Origin" ? "from" : "into"}`,
						reserveHeightForHelperText: false,
					}}
					disabled={!side.nurse || !date || shiftOptions.length <= 1}
				/>
				<OvertimeText ot={side.willCauseOvertime.originOT} />
				<OvertimeText ot={side.willCauseOvertime.swapOT} />
			</Box>
		</Box>
	);
};
